import { PersonStatusTier, type PlayerProfileResponse } from '@on3/api';
import { On3IconLockPremium } from '@on3/icons';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Filter } from '@on3/ui-lib/components/Filter/Filter';
import { Link } from '@on3/ui-lib/components/Link';
import { useAuth } from '@on3/ui-lib/index';
import { baseSports } from '@on3/ui-lib/store/mocks/sports';
import { userHasPermission } from '@on3/ui-lib/utils/auth/access';
import { rdb5Level } from '@on3/ui-lib/utils/auth/constants';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { slugify } from '@on3/ui-lib/utils/slugify';
import clsx from 'clsx';
import { PlayerNav } from 'components/Db/Profile/Header/PlayerNav';
import { VerifiedBadge } from 'components/Elite/VerifiedBadge/VerifiedBadge';
import { getAbbreviatedRank } from 'components/Nil/helpers';
import useAthlete from 'contexts/AthleteProvider';
import { format } from 'date-fns';
import { createTitle } from 'utils/meta';

import { AthleteClaimButton } from '../AthleteClaim/AthleteClaimButton';
import styles from './PlayerHeader.module.scss';

type PlayerHeaderProps = {
  player: PlayerProfileResponse;
  children?: React.ReactNode;
};

export const PlayerHeader = ({ player }: PlayerHeaderProps) => {
  const { user, isVerifiedPlayer } = useAuth();
  const { athlete, athleteInPro, athleteInCollege, athleteInJuco } =
    useAthlete();

  const { formatHref, router } = useDynamicRoute();

  // We should the AthleteType in context and consolidate these checks.
  const showClassRank = player.classRank && (athleteInJuco || athleteInCollege);

  const status =
    player.playerStatus?.transfer &&
    !['Committed', 'Enrolled'].includes(player.playerStatus?.type as string)
      ? 'Transfer'
      : player.playerStatus?.type;

  const committedAsset = player.playerStatus?.committedAsset;
  const canAccessDashboard =
    (isVerifiedPlayer(player.key) || userHasPermission(rdb5Level, user)) &&
    !athleteInPro;

  const showStatus =
    athlete?.playerStatus && status !== 'None' && !athleteInPro;

  const sportOptions = baseSports.filter(
    (sport) => player.sports?.some((s) => s.key === sport.key),
  );
  const playerAbbreviatedSport = sportOptions.find(
    (s) => s.key === player.defaultSport?.key,
  )?.abbreviation;

  const utmParams = new URLSearchParams({
    utm_source: 'player_header',
    utm_medium: 'web',
    utm_campaign: `${slugify(player.name)}`,
    utm_content: 'lock',
  });
  const utmLink = `/rivals/subscribe/?${utmParams}`;

  const hideNil = router.asPath.includes('/nil/');

  const showPlayerNav =
    athlete.tier !== PersonStatusTier.Base &&
    [1, 2, 5, 12].includes((player.defaultSport?.key as number) || 1);

  return (
    <section className={clsx(styles.block)}>
      <figure className={styles.avatar}>
        <Avatar
          altText={player.defaultAsset?.altText}
          domain={player.defaultAsset?.domain}
          fit="cover"
          height={200}
          source={player.defaultAsset?.source}
          width={200}
        />
      </figure>
      <div className={styles.verified}>
        {player.athleteVerified && (
          <VerifiedBadge tier={player.tier} type="dark" />
        )}
        <AthleteClaimButton />
      </div>

      <div className={clsx(styles.meta, styles.classYear)}>
        {!!player.classYear && (
          <dl className={styles.group}>
            <dt>Class</dt>
            <dd>
              {showClassRank
                ? getAbbreviatedRank(player.classRank || '')
                : player.classYear}
            </dd>
          </dl>
        )}
        {!!player.defaultSport && (
          <dl className={styles.group}>
            <dt>Sport</dt>
            {(player.sports ?? []).length > 1 ? (
              <Filter
                className={styles.sportFilter}
                name="sport"
                navigate={(_, v) => formatHref({ query: { sport: v } })}
                options={sportOptions.map((sport) => ({
                  label: sport.abbreviation as string,
                  value: slugify(sport.name),
                }))}
                value={slugify(player.defaultSport.name)}
              />
            ) : (
              <dd
                className={styles.sport}
                data-abbreviated={!!playerAbbreviatedSport}
              >
                {playerAbbreviatedSport ?? player.defaultSport?.name ?? '-'}
              </dd>
            )}
          </dl>
        )}
      </div>
      <div className={clsx(styles.meta, styles.vitals)}>
        {!!player.jerseyNumber && (
          <dl className={styles.group}>
            <dt aria-label="Jersey Number">#</dt>
            <dd>{player.jerseyNumber}</dd>
          </dl>
        )}
        {!!player.positionAbbreviation && (
          <dl className={styles.group}>
            <dt aria-label="Position">Pos</dt>
            <dd>{player.positionAbbreviation}</dd>
          </dl>
        )}
        {!!player.height && (
          <dl className={styles.group}>
            <dt aria-label="Height">Ht</dt>
            <dd>{player.height}</dd>
          </dl>
        )}
        {!!player.weight && (
          <dl className={styles.group}>
            <dt aria-label="Weight">Wt</dt>
            <dd>{player.weight}</dd>
          </dl>
        )}
      </div>
      <div className={clsx(styles.meta, styles.orgs)}>
        {showStatus && (
          <Link
            aria-label={`Visit ${committedAsset?.fullName} On3 page`}
            className={clsx(styles.group, styles.status)}
            fallback={'div'}
            href={committedAsset?.slug && `/college/${committedAsset.slug}/`}
          >
            {committedAsset?.url && (
              <img
                alt={committedAsset?.fullName as string}
                src={committedAsset?.url as string}
              />
            )}
            <dl>
              <dt>Status</dt>
              <dd>{status}</dd>
              {player.playerStatus?.date && (
                <>
                  <dt className="sr-only">Commitment Date</dt>
                  <dd className={styles.committedDate}>
                    {format(new Date(player.playerStatus?.date), 'M/dd/yyyy')}
                  </dd>
                </>
              )}
            </dl>
          </Link>
        )}
        {!!player.highSchool && (
          <Link
            aria-label={`Visit ${player.highSchool.name} Rivals High School page`}
            className={styles.group}
            href={
              player.highSchool?.urlSlug &&
              `/high-school/${player.highSchool.urlSlug}/`
            }
          >
            {player.highSchool.assetUrl && (
              <img
                alt={player.highSchool.name as string}
                src={player.highSchool.assetUrl as string}
              />
            )}
            <dl>
              <dt>High School</dt>
              <dd>
                {createTitle(
                  player.highSchool.knownAs ?? player.highSchool.name,
                  player.hometownName && `(${player.hometownName})`,
                )}
              </dd>
            </dl>
          </Link>
        )}
      </div>
      <div className={styles.extras}>
        {canAccessDashboard && (
          <Link
            className={styles.access}
            forceReload
            href={`/rivals/${player.slug}/elite/network/`}
          >
            Access Private Dashboard
          </Link>
        )}
        {!hideNil && (
          <Link
            aria-label={`Visit ${player.name} N I L Value page`}
            className={styles.nilValue}
            href={
              player.nilValue || user?.has
                ? `/rivals/${player.slug}/nil/`
                : utmLink
            }
          >
            NIL Value
            {player.nilValue ? (
              <span>{formatCurrency(player.nilValue)}</span>
            ) : (
              <>
                {user?.has ? (
                  <span>-</span>
                ) : (
                  <On3IconLockPremium className={styles.lock} />
                )}
              </>
            )}
          </Link>
        )}
      </div>
      {showPlayerNav && (
        <div className={styles.navigation}>
          <PlayerNav />
        </div>
      )}
    </section>
  );
};
